import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Grid,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Close, CheckCircle, Cancel } from "@mui/icons-material";
import "./SelfProductDetailsModal.scss";

const SelfProductDetailsModal = ({
  open,
  onClose,
  productDetail,
  loading,
  onSelectProduct,
  selectedInterestRate,
  setSelectedInterestRate,
}) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedInstallmentPlan, setSelectedInstallmentPlan] = useState(null);
  const [isBNPL, setIsBNPL] = useState(false);

  React.useEffect(() => {
    if (productDetail?.variants?.length > 0) {
      setSelectedVariant(productDetail.variants[0]);
    } else {
      setSelectedVariant(null);
    }

    setSelectedInstallmentPlan(null);
    setIsBNPL(false);
    if (setSelectedInterestRate) {
      setSelectedInterestRate(null);
    }
  }, [productDetail, setSelectedInterestRate]);

  const handleVariantChange = (event) => {
    const variantId = event.target.value;
    const variant = productDetail?.variants?.find(
      (v) => v.variant_id === variantId
    );
    setSelectedVariant(variant);
    setSelectedInstallmentPlan(null);
    setIsBNPL(false);
    if (setSelectedInterestRate) {
      setSelectedInterestRate(null);
    }
  };

  const handleInstallmentPlanChange = (event) => {
    const planValue = event.target.value;

    if (planValue === "pay_full") {
      setSelectedInstallmentPlan(null);
      setIsBNPL(false);
      if (setSelectedInterestRate) {
        setSelectedInterestRate(null);
      }
    } else {
      setSelectedInstallmentPlan(planValue);
      setIsBNPL(true);

      // Check if this is a commission plan and set interest rate
      const commissionPlans = getCommissionPlans();
      const selectedCommissionPlan = commissionPlans.find(
        (plan) => plan.original_text === planValue
      );

      if (selectedCommissionPlan && setSelectedInterestRate) {
        setSelectedInterestRate(selectedCommissionPlan.interest_rate);
      } else if (setSelectedInterestRate) {
        setSelectedInterestRate(null);
      }
    }
  };

  const handleSelect = () => {
    if (selectedVariant && productDetail) {
      onSelectProduct(productDetail, selectedVariant, {
        is_bnpl: isBNPL,
        installment_plan: selectedInstallmentPlan,
      });
      onClose();
    }
  };

  const formatPrice = (price) => {
    if (!price) return "N/A";
    return typeof price === "string" ? price : price.toString();
  };

  const getDeliveryStatus = () => {
    if (productDetail?.pre_order) return "Pre-order";
    if (productDetail?.sold_out) return "Sold Out";
    return "Available";
  };

  const getDeliveryStatusColor = () => {
    if (productDetail?.pre_order) return "warning";
    if (productDetail?.sold_out) return "error";
    return "success";
  };

  const getCommissionPlans = () => {
    const plans = [];

    if (!selectedVariant?.variant_detail) {
      return plans;
    }

    const variantDetail = selectedVariant.variant_detail;

    if (variantDetail.commission_installment_plans?.plans) {
      const commissionPlans = variantDetail.commission_installment_plans.plans;

      commissionPlans.forEach((planObj, index) => {
        // Get processing fee for this plan
        const processingFee =
          variantDetail.commission_installment_plans.processing_fees?.find(
            (fee) => fee.installment_price === planObj.installment_price
          );

        const processingFeeText = processingFee
          ? ` (${processingFee.fees} processing fee)`
          : "";

        plans.push({
          id: `commission-${index}`,
          type: "commission",
          title: `Commission Plan - ${
            planObj.installment_plan.split(" | ")[0]
          }`,
          duration: planObj.installment_plan.split(" | ")[0],
          monthly_amount: planObj.installment_price,
          interest_rate: planObj.interest_rate,
          description: `${planObj.installment_plan}${processingFeeText}`,
          original_text: planObj.installment_plan,
        });
      });
    }

    return plans;
  };

  const getInstallmentPlans = () => {
    const plans = [];

    if (!selectedVariant?.variant_detail) {
      return plans;
    }

    const variantDetail = selectedVariant.variant_detail;

    // Add commission plans using the new structure
    plans.push(...getCommissionPlans());

    // Keep existing logic for bank plans
    if (variantDetail.all_bank_installment_plans) {
      const bankPlans = variantDetail.all_bank_installment_plans;

      if (bankPlans.plans && bankPlans.plans.length > 0) {
        bankPlans.plans.forEach((planText, index) => {
          const [duration, amount] = planText.split(" | ");
          const monthsMatch = duration.match(/(\d+)\s*Months?/i);
          const amountMatch = amount.match(/PKR\s*([\d,]+)/);

          plans.push({
            id: `bank-${index}`,
            type: "bank",
            title: `Bank Plan - ${duration}`,
            duration: monthsMatch ? parseInt(monthsMatch[1]) : null,
            monthly_amount: amountMatch ? amountMatch[1] : null,
            description: planText,
            original_text: planText,
          });
        });
      }
    }

    // Keep existing logic for regular product-level plans
    if (
      productDetail?.installment_plans &&
      productDetail.installment_plans.length > 0
    ) {
      plans.push(
        ...productDetail.installment_plans.map((plan, index) => ({
          ...plan,
          id: plan.id || `product-${index}`,
          type: "regular",
        }))
      );
    }

    if (
      productDetail?.commission_installment_plans &&
      productDetail.commission_installment_plans.length > 0
    ) {
      plans.push(
        ...productDetail.commission_installment_plans.map((plan, index) => ({
          ...plan,
          id: plan.id || `product-commission-${index}`,
          type: "commission",
        }))
      );
    }

    return plans;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Loading product details...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!productDetail) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="product-details-modal"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h2">
            Product Details
          </Typography>
          <Button
            onClick={onClose}
            size="small"
            sx={{ minWidth: "auto", p: 1 }}
          >
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Product Information */}
          <Grid item xs={12} md={12}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {productDetail.title}
              </Typography>

              {/* Brand and Vendor */}
              <Box display="flex" gap={2} mb={2}>
                {productDetail.brand?.[0] && (
                  <Chip
                    label={`Brand: ${productDetail.brand[0].title}`}
                    variant="outlined"
                    color="primary"
                  />
                )}
                {productDetail.vendor && (
                  <Chip
                    label={`Seller: ${productDetail.vendor.name}`}
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Box>

              {/* Status Chips */}
              <Box display="flex" gap={1} mb={2}>
                <Chip
                  label={getDeliveryStatus()}
                  color={getDeliveryStatusColor()}
                  icon={productDetail.sold_out ? <Cancel /> : <CheckCircle />}
                />
                {productDetail.delivery_date && (
                  <Chip
                    label={`Delivery: ${productDetail.delivery_date}`}
                    variant="outlined"
                  />
                )}
                {productDetail.exchange && (
                  <Chip
                    label={productDetail.exchange}
                    variant="outlined"
                    color="info"
                  />
                )}
              </Box>

              {/* Description */}
              {productDetail.description && (
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    dangerouslySetInnerHTML={{
                      __html: productDetail.description,
                    }}
                  />
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Variant Selection */}
              {productDetail.variants && productDetail.variants.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {productDetail.variants.length > 1
                      ? "Select Variant"
                      : "Product Variant"}
                  </Typography>

                  {productDetail.variants.length > 1 ? (
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        value={selectedVariant?.variant_id || ""}
                        onChange={handleVariantChange}
                      >
                        {productDetail.variants.map((variant) => (
                          <FormControlLabel
                            key={variant.variant_id}
                            value={variant.variant_id}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="body1">
                                  {variant.title}
                                  {variant.option1 && ` - ${variant.option1}`}
                                  {variant.option2 && ` - ${variant.option2}`}
                                  {variant.option3 && ` - ${variant.option3}`}
                                </Typography>
                                <Typography variant="body2" color="primary">
                                  Price: Rs.{" "}
                                  {formatPrice(
                                    variant.variant_detail?.discounted_price ||
                                      variant.variant_detail?.original_price
                                  )}
                                  {variant.variant_detail?.discounted_price &&
                                    variant.variant_detail?.original_price !==
                                      variant.variant_detail
                                        ?.discounted_price && (
                                      <span
                                        style={{
                                          textDecoration: "line-through",
                                          marginLeft: "8px",
                                          color: "gray",
                                        }}
                                      >
                                        Rs.{" "}
                                        {formatPrice(
                                          variant.variant_detail?.original_price
                                        )}
                                      </span>
                                    )}
                                </Typography>
                                {variant.sku && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    SKU: {variant.sku}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  ) : (
                    // Single variant display
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body1" gutterBottom>
                        {productDetail.variants[0].title}
                        {productDetail.variants[0].option1 &&
                          ` - ${productDetail.variants[0].option1}`}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        Rs.{" "}
                        {formatPrice(
                          productDetail.variants[0].variant_detail
                            ?.discounted_price ||
                            productDetail.variants[0].variant_detail
                              ?.original_price
                        )}
                        {productDetail.variants[0].variant_detail
                          ?.discounted_price &&
                          productDetail.variants[0].variant_detail
                            ?.original_price !==
                            productDetail.variants[0].variant_detail
                              ?.discounted_price && (
                            <span
                              style={{
                                textDecoration: "line-through",
                                marginLeft: "8px",
                                color: "gray",
                                fontSize: "0.9rem",
                              }}
                            >
                              Rs.{" "}
                              {formatPrice(
                                productDetail.variants[0].variant_detail
                                  ?.original_price
                              )}
                            </span>
                          )}
                      </Typography>
                      {productDetail.variants[0].sku && (
                        <Typography variant="caption" color="text.secondary">
                          SKU: {productDetail.variants[0].sku}
                        </Typography>
                      )}
                    </Card>
                  )}
                </Box>
              )}

              {/* BNPL / Installment Plans Section */}
              {selectedVariant && getInstallmentPlans().length > 0 && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Payment Options{" "}
                    {isBNPL && (
                      <Chip
                        label="BNPL Selected"
                        color="success"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {selectedInterestRate && (
                      <Chip
                        label={`Interest Rate: ${selectedInterestRate}%`}
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>

                  <FormControl component="fieldset" fullWidth>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ mb: 2 }}
                    >
                      Choose your preferred payment method
                    </Typography>

                    <RadioGroup
                      value={selectedInstallmentPlan || "pay_full"}
                      onChange={handleInstallmentPlanChange}
                      sx={{ gap: 1 }}
                    >
                      {/* Full Payment Option */}
                      <Card
                        variant="outlined"
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <FormControlLabel
                          value="pay_full"
                          control={<Radio />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Typography variant="body1" fontWeight="medium">
                                Pay Full Amount
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                One-time payment - No installments
                              </Typography>
                            </Box>
                          }
                          sx={{ margin: 0, width: "100%" }}
                        />
                      </Card>

                      {/* Commission Plans */}
                      {getInstallmentPlans().filter(
                        (plan) => plan.type === "commission"
                      ).length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            color="red"
                            gutterBottom
                          >
                            Bank Alfalah Plans
                          </Typography>
                          {getInstallmentPlans()
                            .filter((plan) => plan.type === "commission")
                            .map((plan) => (
                              <Card
                                key={plan.id}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  mb: 1,
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: "action.hover" },
                                }}
                              >
                                <FormControlLabel
                                  value={plan.original_text}
                                  control={<Radio />}
                                  label={
                                    <Box sx={{ ml: 1 }}>
                                      <Typography
                                        variant="body1"
                                        fontWeight="medium"
                                      >
                                        {plan.title}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {plan.description}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ margin: 0, width: "100%" }}
                                />
                              </Card>
                            ))}
                        </Box>
                      )}

                      {/* Bank Plans */}
                      {getInstallmentPlans().filter(
                        (plan) => plan.type === "bank"
                      ).length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            color="red"
                            gutterBottom
                          >
                            All Bank Plans
                          </Typography>
                          {getInstallmentPlans()
                            .filter((plan) => plan.type === "bank")
                            .map((plan) => (
                              <Card
                                key={plan.id}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  mb: 1,
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: "action.hover" },
                                }}
                              >
                                <FormControlLabel
                                  value={plan.original_text}
                                  control={<Radio />}
                                  label={
                                    <Box sx={{ ml: 1 }}>
                                      <Typography
                                        variant="body1"
                                        fontWeight="medium"
                                      >
                                        {plan.title}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {plan.description}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ margin: 0, width: "100%" }}
                                />
                              </Card>
                            ))}
                        </Box>
                      )}

                      {/* Regular/Product Level Plans (fallback) */}
                      {getInstallmentPlans().filter(
                        (plan) => plan.type === "regular"
                      ).length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            color="info.main"
                            gutterBottom
                          >
                            Other Plans
                          </Typography>
                          {getInstallmentPlans()
                            .filter((plan) => plan.type === "regular")
                            .map((plan) => (
                              <Card
                                key={plan.id}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  mb: 1,
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: "action.hover" },
                                }}
                              >
                                <FormControlLabel
                                  value={plan.original_text}
                                  control={<Radio />}
                                  label={
                                    <Box sx={{ ml: 1 }}>
                                      <Typography
                                        variant="body1"
                                        fontWeight="medium"
                                      >
                                        {plan.title ||
                                          plan.name ||
                                          `${
                                            plan.installments || plan.duration
                                          } Installments`}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {plan.description ||
                                          `${
                                            plan.installments || plan.duration
                                          } monthly payments`}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ margin: 0, width: "100%" }}
                                />
                              </Card>
                            ))}
                        </Box>
                      )}
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}

              {/* Additional Product Info */}
              <Box mt={2}>
                <Grid container spacing={2}>
                  {productDetail.loyalty_points > 0 && (
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Loyalty Points:</strong>{" "}
                        {productDetail.loyalty_points}
                      </Typography>
                    </Grid>
                  )}
                  {selectedVariant?.weight && (
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Weight:</strong> {selectedVariant.weight}{" "}
                        {selectedVariant.weight_unit}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Cancel
        </Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          size="large"
          disabled={!selectedVariant || productDetail.sold_out}
        >
          {productDetail.sold_out ? "Sold Out" : "Select Product"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelfProductDetailsModal;
